import { makeStateStore } from '@playcastdotio/StateStore';
import { PlaycastDefaultProfiles } from './profiles';
import { PlaycastWebrtcStats } from './typesMedia';
import { isMobileDevice } from './util';

const QUALITY_PROFILES: PlaycastDefaultProfiles[] = ['hostLow', 'hostMedium', 'hostHigh'];
const OSCILLATION_WINDOW = 60000; // 1 minute window to check for oscillations
const MAX_CHANGES_IN_WINDOW = 3; // Maximum number of quality changes allowed in the oscillation window
const DECREASE_COOLDOWN = 2000; // 2 seconds cooldown for decreasing quality
const INCREASE_COOLDOWN = 10000; // 10 seconds cooldown for increasing quality
const OSCILLATION_THRESHOLD = 2; // Number of back-and-forth changes to consider as oscillation
const OSCILLATION_PENALTY = 30000; // Additional cooldown when oscillation is detected
const PANIC_COOLDOWN = 2000; // n seconds cooldown for panicking

export const createQualityManager = () => {
  const initialPreferredQuality = {
    auto: true,
    // profile: 'auto' as PlaycastDefaultProfiles,
    profile: 'hostHigh' as PlaycastDefaultProfiles,
  };
  const preferredQualityState = makeStateStore(initialPreferredQuality);

  if (isMobileDevice()) {
    QUALITY_PROFILES.pop(); // Remove 'hostHigh' from the list of quality profiles for mobile devices
  }

  let lastChangeTimestamp = 0;
  let changeHistory: { timestamp: number; direction: 'increase' | 'decrease' }[] = [];
  let oscillationPenalty = 0;

  let panicCallback: ((reason: 'fps' | 'bitrate') => void) | null = null;
  let lastPanicTime = 0;

  const canChangeQuality = (direction: 'increase' | 'decrease'): boolean => {
    const now = Date.now();
    const cooldown = direction === 'decrease' ? DECREASE_COOLDOWN : INCREASE_COOLDOWN + oscillationPenalty;

    // Check cooldown period
    if (now - lastChangeTimestamp < cooldown) {
      return false;
    }

    // Remove old entries from change history
    changeHistory = changeHistory.filter((change) => now - change.timestamp < OSCILLATION_WINDOW);

    // Check if we've exceeded the maximum number of changes in the window
    return changeHistory.length < MAX_CHANGES_IN_WINDOW;
  };

  const recordChange = (direction: 'increase' | 'decrease'): void => {
    const now = Date.now();
    lastChangeTimestamp = now;
    changeHistory.push({ timestamp: now, direction });

    // Check for oscillation
    if (changeHistory.length >= OSCILLATION_THRESHOLD * 2) {
      const recentChanges = changeHistory.slice(-OSCILLATION_THRESHOLD * 2);
      let isOscillating = true;
      for (let i = 0; i < recentChanges.length - 1; i += 2) {
        if (recentChanges[i].direction === recentChanges[i + 1].direction) {
          isOscillating = false;
          break;
        }
      }
      if (isOscillating && direction === 'increase') {
        oscillationPenalty = OSCILLATION_PENALTY;
      }
    }
  };

  const getNextProfile = (currentProfile: PlaycastDefaultProfiles, direction: 'increase' | 'decrease'): PlaycastDefaultProfiles => {
    const currentIndex = QUALITY_PROFILES.indexOf(currentProfile);
    if (direction === 'increase' && currentIndex < QUALITY_PROFILES.length - 1) {
      return QUALITY_PROFILES[currentIndex + 1];
    } else if (direction === 'decrease' && currentIndex > 0) {
      return QUALITY_PROFILES[currentIndex - 1];
    }
    return currentProfile; // No change if at the limits
  };

  const parseQualityStats = (qualityStats: PlaycastWebrtcStats['quality']): void => {
    const currentState = preferredQualityState.currentState;
    if (!currentState.auto) {
      return;
    }

    const { recommendation, reason } = qualityStats;

    return;

    let newProfile = currentState.profile;

    switch (recommendation) {
      case 'increase':
        if (canChangeQuality('increase')) {
          newProfile = getNextProfile(currentState.profile, 'increase');
        }
        break;
      case 'decrease':
        if (canChangeQuality('decrease')) {
          newProfile = getNextProfile(currentState.profile, 'decrease');
        }
        break;
      case 'panic':
        if (canChangeQuality('decrease')) {
          newProfile = getNextProfile(currentState.profile, 'decrease');
        }
        if (panicCallback) {
          if (Date.now() - lastPanicTime > PANIC_COOLDOWN) {
            // number of times it has been panicking sequentially
            const reasonType = reason === 'FRAMERATE_PANIC' ? 'fps' : 'bitrate';
            // panicCallback(reasonType);
            lastPanicTime = Date.now();
          }
        }
        break;
      case 'maintain':
        // No change
        return;
    }

    if (newProfile !== currentState.profile) {
      const change = recommendation === 'increase' ? 'increase' : 'decrease';
      recordChange(change);
      preferredQualityState.updateState({ ...currentState, profile: newProfile });
    }
  };

  const increaseQuality = (): void => {
    const currentState = preferredQualityState.currentState;
    if (currentState.auto && canChangeQuality('increase')) {
      const newProfile = getNextProfile(currentState.profile, 'increase');
      preferredQualityState.updateState({ ...currentState, profile: newProfile });
      recordChange('increase');
    }
  };

  const decreaseQuality = (): void => {
    const currentState = preferredQualityState.currentState;
    if (currentState.auto && canChangeQuality('decrease')) {
      const newProfile = getNextProfile(currentState.profile, 'decrease');
      preferredQualityState.updateState({ ...currentState, profile: newProfile });
      recordChange('decrease');
    }
  };

  const setQuality = (profile: PlaycastDefaultProfiles, auto?: boolean): void => {
    preferredQualityState.updateState({ profile, auto: auto ?? true });
    oscillationPenalty = 0; // Reset oscillation penalty when manually setting quality
  };

  const onFrameratePanic = (cb: (reason: 'fps' | 'bitrate') => void): void => {
    panicCallback = cb;
  };

  return { parseQualityStats, preferredQualityState, increaseQuality, decreaseQuality, setQuality, onFrameratePanic };
};
